<script setup lang="ts">
import { calendarDate, formatEventDate, resolveEventDate, type DatePrecision, type Season } from '../../client-db/src/event-date'
import { dateClarificationQuestion, clarificationYearOptions, completeClarificationDate } from '../../client-db/src/event-date-clarification'

import {
  ref,
  onMounted,
  computed,
  useTemplateRef,
  watch,
  onBeforeUnmount,
  nextTick,
  h,
  render,
} from 'vue'
import { useEventStore } from '@/stores/event-store.ts'
import dayjs from 'dayjs'
import TextEditor from '@/components/TextEditor.vue'
import DotAvatar from '@/components/DotAvatar.vue'
import DateYearPicker from '@/components/DateYearPicker.vue'
import EventCollaborationPanel from '@/components/EventCollaborationPanel.vue'
import EventLocationPicker from '@/components/EventLocationPicker.vue'
import { useEventFiles } from '@/stores/event-files-composable.ts'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import {
  IconTrash,
  IconPlant,
  IconSun,
  IconLeaf,
  IconSnowflake,
  IconCalendar,
  IconCalendarMonth,
  IconMapPinFilled,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconShare2,
  IconMessageCircle,
  IconDotsCircleHorizontal,
  IconShare,
  IconTag,
  IconUserPlus,
  IconListCheck,
  IconPhotoPlus,
  IconX,
} from '@tabler/icons-vue'
import 'photoswipe/style.css'
import { localDB } from '@/migrate.ts'
import {
  eventPeopleTable,
  eventsTable,
  eventTagsTable,
  locationsTable,
  peopleTable,
  tagsTable,
} from '../../client-db/src/db/schema.ts'
import { asc, eq } from 'drizzle-orm'
import { getInitials, loadPeopleUiMetaFromStorage } from '@/utils/people-avatar.ts'
import { matchesTripCandidate, parseTripTemporalAnchor } from '@/utils/trip-temporal-anchor.ts'

const eventStore = useEventStore()

const {
  eventImages,
  selectedImageIndex,
  fileInput,
  pendingImageUploads,
  isUploadingImages,
  loadEventImages,
  loadFullSizeImage,
  getImageSource,
  deleteCurrentImage,
  triggerImageUpload,
  handleImageUpload,
  clearEventFiles,
} = useEventFiles()

const eventTitleEl = ref()
const eventTitleText = ref()
const originalTitleValue = ref()
const eventDescriptionEl = ref()
const eventDescriptionText = ref()
const originalDescriptionValue = ref()
const deleteEventModal = useTemplateRef('deleteEventModal')
const unsavedChangesModal = useTemplateRef('unsavedChangesModal')
const deleteImageConfirmModal = useTemplateRef('deleteImageConfirmModal')
const textEditor = ref()
let lightbox: PhotoSwipeLightbox | null = null
const pendingDeleteIndex = ref<number | null>(null)
const photoSwipeItems = ref<Array<{ src: string; width: number; height: number; alt: string }>>([])
const dimensionCache = new Map<string, { width: number; height: number }>()
const parentEventLabel = ref<string | null>(null)
const parentEventId = ref<number | null>(null)
const childEventCount = ref(0)
const tripAnchorDestination = ref<string | null>(null)
const tripAnchorCandidates = ref<Array<{ id: number; title: string; endDate: Date | null; date: Date | null }>>([])
const linkedTripTitle = ref<string | null>(null)
const isDatePopoverOpen = ref(false)
const isLocationPickerOpen = ref(false)
const collaborationPanelRef = ref<{ openModal: () => void } | null>(null)
const unreadCollaborateCount = ref(0)
const selectedCollaborateItemsCount = ref(0)
const availablePeople = ref<string[]>([])
const peopleAvatarByName = ref<Record<string, string>>({})
const availableTags = ref<string[]>([])

// Track text editing state
const textEditingStartContent = ref('')
const isTextEditorFocused = ref(false)
const activeDetailTab = ref<'event' | 'collaboration'>('event')

onMounted(() => {
  eventTitleText.value = eventStore.currentEvent?.title || ''
  if (eventTitleEl.value) {
    eventTitleEl.value.innerText = eventTitleText.value
  }
  eventDescriptionText.value = eventStore.currentEvent?.description || ''
  if (eventDescriptionEl.value) {
    eventDescriptionEl.value.innerText = eventDescriptionText.value
  }
  eventStore.startEditing()
})

watch(
  () => [eventStore.currentEvent?.id, eventStore.showEvent] as const,
  async ([eventId, isEventOpen]) => {
    if (!isEventOpen || !eventId) {
      parentEventLabel.value = null
      parentEventId.value = null
      childEventCount.value = 0
      tripAnchorDestination.value = null
      tripAnchorCandidates.value = []
      linkedTripTitle.value = null
      return
    }
    eventTitleText.value = eventStore.currentEvent?.title || ''
    if (eventTitleEl.value) {
      eventTitleEl.value.innerText = eventTitleText.value
    }
    eventDescriptionText.value = eventStore.currentEvent?.description || ''
    if (eventDescriptionEl.value) {
      eventDescriptionEl.value.innerText = eventDescriptionText.value
    }
    dimensionCache.clear()
    photoSwipeItems.value = []
    await loadLookupOptions()
    await loadEventMetadata(eventId)
    await loadEventImages()
    await loadGroupingMetadata()
    await loadTripAnchorMetadata()
    await focusTextEditorIfEmpty()
  },
  {
    immediate: true,
    // Prevent false->true toggles in the same tick from being coalesced away.
    flush: 'sync',
  },
)

async function focusTextEditorIfEmpty() {
  if (!eventStore.showEvent || activeDetailTab.value !== 'event') {
    return
  }

  const currentText = eventStore.currentEvent?.text?.trim() || ''
  if (currentText.length > 0) {
    return
  }

  await nextTick()
  textEditor.value?.focusEditor?.()
}

async function loadLookupOptions() {
  const [peopleRows, tagRows] = await Promise.all([
    localDB
      .select({ id: peopleTable.id, fullName: peopleTable.fullName })
      .from(peopleTable)
      .orderBy(asc(peopleTable.fullName)),
    localDB.select({ name: tagsTable.name }).from(tagsTable).orderBy(asc(tagsTable.name)),
  ])

  const peopleUiMetaById = loadPeopleUiMetaFromStorage()
  const avatarByName: Record<string, string> = {}
  const peopleNames: string[] = []

  for (const row of peopleRows) {
    const trimmedName = row.fullName?.trim()
    if (!trimmedName) continue

    peopleNames.push(trimmedName)

    const avatarUrl = peopleUiMetaById[String(row.id)]?.avatarUrl
    if (typeof avatarUrl === 'string' && avatarUrl && !avatarByName[trimmedName]) {
      avatarByName[trimmedName] = avatarUrl
    }
  }

  availablePeople.value = peopleNames
  peopleAvatarByName.value = avatarByName
  availableTags.value = tagRows
    .map((row) => row.name?.trim())
    .filter((value): value is string => Boolean(value))
}

async function loadEventMetadata(eventId: number) {
  const [linkedPeople, linkedTags] = await Promise.all([
    localDB
      .select({ fullName: peopleTable.fullName })
      .from(eventPeopleTable)
      .innerJoin(peopleTable, eq(eventPeopleTable.personId, peopleTable.id))
      .where(eq(eventPeopleTable.eventId, eventId)),
    localDB
      .select({ name: tagsTable.name })
      .from(eventTagsTable)
      .innerJoin(tagsTable, eq(eventTagsTable.tagId, tagsTable.id))
      .where(eq(eventTagsTable.eventId, eventId)),
  ])

  const [eventLocation] = await localDB
    .select({ name: locationsTable.name, locationId: locationsTable.id })
    .from(eventsTable)
    .leftJoin(locationsTable, eq(eventsTable.locationId, locationsTable.id))
    .where(eq(eventsTable.id, eventId))
    .limit(1)

  if (!eventStore.currentEvent || eventStore.currentEvent.id !== eventId) {
    return
  }

  const linkedPeopleNames = linkedPeople
    .map((person) => person.fullName?.trim())
    .filter((value): value is string => Boolean(value))
  const linkedTagNames = linkedTags
    .map((tag) => tag.name?.trim())
    .filter((value): value is string => Boolean(value))
  const linkedLocationNames = eventLocation?.name ? [eventLocation.name] : []

  if (linkedPeopleNames.length > 0 || (eventStore.currentEvent.people?.length ?? 0) === 0) {
    eventStore.currentEvent.people = linkedPeopleNames
  }

  if (linkedTagNames.length > 0 || (eventStore.currentEvent.tags?.length ?? 0) === 0) {
    eventStore.currentEvent.tags = linkedTagNames
  }

  if (linkedLocationNames.length > 0 || (eventStore.currentEvent.locations?.length ?? 0) === 0) {
    eventStore.currentEvent.locations = linkedLocationNames
    eventStore.currentEvent.locationId = eventLocation?.locationId ?? null
  }

  eventStore.startEditing()
}

async function loadGroupingMetadata() {
  const currentEventId = eventStore.currentEvent?.id

  if (!currentEventId) {
    parentEventLabel.value = null
    parentEventId.value = null
    childEventCount.value = 0
    return
  }

  const childEvents = await localDB
    .select({ id: eventsTable.id })
    .from(eventsTable)
    .where(eq(eventsTable.parentEventId, currentEventId))
  childEventCount.value = childEvents.length

  const currentParentId = eventStore.currentEvent?.parentEventId
  if (!currentParentId) {
    parentEventLabel.value = null
    parentEventId.value = null
    return
  }

  const [parentEvent] = await localDB
    .select({ title: eventsTable.title, date: eventsTable.date })
    .from(eventsTable)
    .where(eq(eventsTable.id, currentParentId))
    .limit(1)

  if (!parentEvent) {
    parentEventLabel.value = null
    parentEventId.value = null
    return
  }

  const parentDate = parentEvent.date ? dayjs(parentEvent.date).format('MM/DD/YYYY') : null
  parentEventId.value = currentParentId
  parentEventLabel.value = parentDate
    ? `${parentEvent.title || 'Untitled event'} (${parentDate})`
    : parentEvent.title || 'Untitled event'
}

async function loadTripAnchorMetadata() {
  const current = eventStore.currentEvent
  const phrase = parseTripTemporalAnchor(current?.originalText ?? '')
  tripAnchorDestination.value = phrase?.destination ?? null
  tripAnchorCandidates.value = []
  linkedTripTitle.value = null
  if (!current?.id) return

  const rows = await localDB.select({
    id: eventsTable.id,
    title: eventsTable.title,
    date: eventsTable.date,
    endDate: eventsTable.endDate,
    scopeId: eventsTable.scopeId,
  }).from(eventsTable)
  const linked = rows.find(row => row.id === current.afterEventEndId)
  linkedTripTitle.value = linked?.title ?? null
  if (!phrase) return
  tripAnchorCandidates.value = rows
    .filter(row => row.id !== current.id && row.scopeId === current.scopeId && matchesTripCandidate(row.title, phrase.destination))
    .map(row => ({ id: row.id, title: row.title!, date: row.date, endDate: row.endDate }))
}

async function selectTripAnchor(id: number | null) {
  if (!eventStore.currentEvent?.id || (id && !tripAnchorCandidates.value.some(candidate => candidate.id === id))) return
  eventStore.updateEventInMemory('afterEventEndId', id)
  if (await eventStore.saveChanges()) await loadTripAnchorMetadata()
}

async function handleOpenImageModal(imageIndex: number) {
  if (eventImages.value.length === 0 || !lightbox) {
    return
  }

  photoSwipeItems.value = buildPhotoSwipeItems()
  await primePhotoSwipeItem(imageIndex)
  lightbox.loadAndOpen(imageIndex, photoSwipeItems.value)

  void ensureImageLoadedForLightbox(imageIndex)
  void preloadAdjacentImages(imageIndex)
}

function openDeleteImageConfirm(imageIndex: number) {
  if (eventImages.value.length === 0) {
    return
  }

  pendingDeleteIndex.value = imageIndex
  deleteImageConfirmModal.value?.showModal()
}

async function confirmDeleteImage() {
  if (pendingDeleteIndex.value === null) {
    return
  }

  selectedImageIndex.value = pendingDeleteIndex.value
  const wasLightboxOpen = Boolean(lightbox?.pswp)

  await deleteCurrentImage({
    close: () => {
      lightbox?.pswp?.close()
    },
  })

  deleteImageConfirmModal.value?.close()
  pendingDeleteIndex.value = null

  if (wasLightboxOpen) {
    lightbox?.pswp?.close()

    if (eventImages.value.length > 0) {
      await nextTick()
      await handleOpenImageModal(selectedImageIndex.value)
    }
  }
}

function buildPhotoSwipeItems() {
  return eventImages.value.map((_, index) => ({
    src: getImageSource(index),
    width: getCachedWidth(getImageSource(index)),
    height: getCachedHeight(getImageSource(index)),
    alt: `Event image ${index + 1}`,
  }))
}

async function primePhotoSwipeItem(index: number) {
  const src = getImageSource(index)
  if (!src) {
    return
  }

  await updateItemDimensions(index, src)
}

async function ensureImageLoadedForLightbox(index: number) {
  if (index < 0 || index >= eventImages.value.length) {
    return
  }

  await loadFullSizeImage(index)
  const latestSrc = getImageSource(index)
  const item = photoSwipeItems.value[index]

  if (!item) {
    return
  }

  const srcChanged = item.src !== latestSrc
  item.src = latestSrc
  await updateItemDimensions(index, latestSrc)

  const pswp = lightbox?.pswp as any
  if (srcChanged) {
    pswp?.refreshSlideContent?.(index)
  } else {
    pswp?.invalidateCurrItems?.()
    pswp?.updateSize?.(true)
  }
}

async function preloadAdjacentImages(index: number) {
  const total = eventImages.value.length
  if (total <= 1) {
    return
  }

  const nextIndex = (index + 1) % total
  const prevIndex = (index - 1 + total) % total
  await Promise.all([
    ensureImageLoadedForLightbox(nextIndex),
    ensureImageLoadedForLightbox(prevIndex),
  ])
}

async function updateItemDimensions(index: number, src: string) {
  const item = photoSwipeItems.value[index]
  if (!item || !src) {
    return
  }

  const cached = dimensionCache.get(src)
  if (cached) {
    item.width = cached.width
    item.height = cached.height
    return
  }

  const dimensions = await getImageDimensions(src)
  dimensionCache.set(src, dimensions)
  item.width = dimensions.width
  item.height = dimensions.height
}

function getImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const image = new Image()

    image.onload = () => {
      resolve({
        width: image.naturalWidth || 1200,
        height: image.naturalHeight || 1200,
      })
    }

    image.onerror = () => {
      resolve({
        width: 1200,
        height: 1200,
      })
    }

    image.src = src
  })
}

function getCachedWidth(src: string) {
  return dimensionCache.get(src)?.width ?? 1200
}

function getCachedHeight(src: string) {
  return dimensionCache.get(src)?.height ?? 1200
}

function onTitleFocus() {
  originalTitleValue.value = eventTitleEl.value.innerText
}

function onTitleBlur() {
  if (eventTitleEl.value.innerText !== originalTitleValue.value) {
    commitInlineHeaderEdits()
  }
}

function onDescriptionFocus() {
  originalDescriptionValue.value = eventDescriptionEl.value.innerText
}

function onDescriptionBlur() {
  if (eventDescriptionEl.value.innerText !== originalDescriptionValue.value) {
    commitInlineHeaderEdits()
  }
}

function commitInlineHeaderEdits() {
  if (!eventStore.currentEvent) return

  if (eventTitleEl.value) {
    const nextTitle = eventTitleEl.value.innerText
    const currentTitle = eventStore.currentEvent.title || ''
    if (nextTitle !== currentTitle) {
      eventTitleText.value = nextTitle
      eventStore.updateEventInMemory('title', nextTitle)
    }
  }

  if (eventDescriptionEl.value) {
    const nextDescription = eventDescriptionEl.value.innerText
    const currentDescription = eventStore.currentEvent.description || ''
    if (nextDescription !== currentDescription) {
      eventDescriptionText.value = nextDescription
      eventStore.updateEventInMemory('description', nextDescription)
    }
  }
}

function changeText() {
  eventTitleText.value = eventTitleEl.value.innerText = ''
}

const headerDateLabel = computed(() => {
  const event = eventStore.currentEvent
  if (!event) return 'Date unknown'
  if (!event.datePrecision && event.date && event.endDate && dayjs(event.date).isSame(dayjs(event.endDate), 'day')) {
    return `${formatEventDate(event)} ${dayjs(event.date).format('h:mm A')} - ${dayjs(event.endDate).format('h:mm A')}`
  }
  return formatEventDate(event)
})
const dateDraftPrecision = ref<DatePrecision | 'legacy' | 'month_day'>('unknown')
const dateDraftDay = ref('')
const dateDraftRangeEnd = ref('')
const dateDraftYear = ref<number | undefined>()
const dateDraftMonth = ref(1)
const dateDraftSeason = ref<Season>('summer')
const dateDraftStart = ref('')
const dateDraftEnd = ref('')
const dateMoreOptionsOpen = ref(false)
const dateEditorStep = ref<'choose' | 'season' | 'year' | 'date' | 'month' | 'advanced' | 'review'>('choose')
const dateDetailLabel = computed(() => ({ year: 'Just year', season: 'Season & year', day: 'Exact date', month: 'Month & year', month_day: 'Add the missing year', range: 'Approximate range', legacy: 'Existing date / duration', unknown: 'Not sure yet' })[dateDraftPrecision.value])
const selectedSeasonLabel = computed(() => seasonChoices.find(season => season.value === dateDraftSeason.value)?.label ?? '')
const dateDetailIcon = computed(() => dateDraftPrecision.value === 'season' ? IconSun : dateDraftPrecision.value === 'day' ? IconCalendarEvent : dateDraftPrecision.value === 'month' ? IconCalendarMonth : IconCalendar)
const selectedSeasonIcon = computed(() => seasonChoices.find(season => season.value === dateDraftSeason.value)?.icon ?? IconSun)
const datePreview = computed(() => {
  if (dateDraftPrecision.value === 'legacy') return null
  if (dateDraftPrecision.value === 'month_day') {
    const clue = eventStore.currentEvent?.dateClarification
    return resolveEventDate(clue ? completeClarificationDate(clue, dateDraftYear.value) : { precision: 'unknown' })
  }
  return resolveEventDate({ precision: dateDraftPrecision.value, date: dateDraftDay.value, endDate: dateDraftRangeEnd.value, year: dateDraftYear.value, month: dateDraftMonth.value, season: dateDraftSeason.value })
})
function chooseDateDetail(precision: typeof dateDraftPrecision.value) {
  dateDraftPrecision.value = precision
  dateMoreOptionsOpen.value = false
  dateSaveError.value = ''
  dateEditorStep.value = precision === 'season' ? 'season' : ['year', 'month_day'].includes(precision) ? 'year'
    : precision === 'day' ? 'date' : precision === 'month' ? 'month'
      : ['legacy', 'range'].includes(precision) ? 'advanced' : 'choose'
}

const monthChoices = Array.from({ length: 12 }, (_, index) => ({ value: index + 1, label: new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, index, 1))) }))
const seasonChoices = [
  { value: 'spring' as const, label: 'Spring', icon: IconPlant },
  { value: 'summer' as const, label: 'Summer', icon: IconSun },
  { value: 'fall' as const, label: 'Fall', icon: IconLeaf },
  { value: 'winter' as const, label: 'Winter', icon: IconSnowflake },
]
const editorClarification = computed(() => ['unknown', 'range'].includes(eventStore.currentEvent?.datePrecision ?? '') ? eventStore.currentEvent?.dateClarification : null)
const dateShortcuts = computed(() => {
  const clue = editorClarification.value
  if (clue?.kind === 'month_choices') return clue.monthOptions.map(option => resolveEventDate({ precision: 'month', ...option }))
  return clarificationYearOptions(clue, eventStore.currentEvent).map(year => resolveEventDate(clue?.kind === 'month_day'
    ? completeClarificationDate(clue, year) : { precision: 'year', year }))
})

const dateSaveError = ref('')
const isSavingDate = ref(false)
const clarificationError = ref('')
const isDismissingClarification = ref(false)
const pendingDateClarification = computed(() => {
  const event = eventStore.currentEvent
  return (event?.datePrecision === 'unknown' || (event?.datePrecision === 'range' && ['age_year', 'year_range', 'month_choices'].includes(event.dateClarification?.kind ?? ''))) && event.dateClarification?.status === 'pending'
    ? event.dateClarification : null
})
watch(() => eventStore.currentEvent?.id, () => { clarificationError.value = '' })

function answerDateClarification() {
  const clarification = pendingDateClarification.value
  if (!clarification) return
  toggleDatePopover()
  dateDraftPrecision.value = clarification.kind === 'month_day' ? 'month_day' : clarification.kind === 'season_year' ? 'season' : 'year'
  if (clarification.kind === 'month_day') dateDraftYear.value = undefined
  if (clarification.season) dateDraftSeason.value = clarification.season
  chooseDateDetail(dateDraftPrecision.value)
}

async function chooseClarificationYear(year: number) {
  const clarification = pendingDateClarification.value
  if (!clarificationYearOptions(clarification, eventStore.currentEvent).includes(year) || isSavingDate.value) return
  clarificationError.value = ''
  isSavingDate.value = true
  try {
    const date = clarification?.kind === 'month_day'
      ? completeClarificationDate(clarification, year) : { precision: 'year' as const, year }
    if (!await eventStore.saveEventDate(resolveEventDate(date))) {
      clarificationError.value = 'Could not save the date. Please try again.'
    }
  } finally {
    isSavingDate.value = false
  }
}

async function chooseClarificationMonth(month: number, year: number) {
  const clarification = pendingDateClarification.value
  if (clarification?.kind !== 'month_choices' || isSavingDate.value || !clarification.monthOptions.some(option => option.month === month && option.year === year)) return
  clarificationError.value = ''
  isSavingDate.value = true
  try {
    if (!await eventStore.saveEventDate(resolveEventDate({ precision: 'month', month, year }))) {
      clarificationError.value = 'Could not save the date. Please try again.'
    }
  } finally {
    isSavingDate.value = false
  }
}

async function maybeLater() {
  isDismissingClarification.value = true
  clarificationError.value = ''
  try {
    if (!await eventStore.dismissDateClarification()) {
      clarificationError.value = 'Could not save your choice. Please try again.'
    }
  } finally {
    isDismissingClarification.value = false
  }
}
const hasLegacyDates = computed(() => Boolean(eventStore.currentEvent?.date && !eventStore.currentEvent.datePrecision))

async function saveDateDraft() {
  if (!eventStore.currentEvent || isSavingDate.value) return
  dateSaveError.value = ''
  let next: { date: Date | null; endDate: Date | null; datePrecision: DatePrecision | null }
  if (dateDraftPrecision.value === 'legacy') {
    const start = dateDraftStart.value ? dayjs(dateDraftStart.value).toDate() : null
    const end = dateDraftEnd.value ? dayjs(dateDraftEnd.value).toDate() : null
    if (!start || Number.isNaN(start.getTime()) || (end && (Number.isNaN(end.getTime()) || end < start))) {
      dateSaveError.value = 'Enter valid dates, with the end after the start.'
      return
    }
    next = { date: start, endDate: end, datePrecision: null }
  } else if (dateDraftPrecision.value === 'month_day') {
    const clarification = pendingDateClarification.value
    next = resolveEventDate(clarification ? completeClarificationDate(clarification, dateDraftYear.value) : null)
    if (next.datePrecision === 'unknown') {
      dateSaveError.value = 'Enter a valid year for this date.'
      return
    }
  } else {
    next = resolveEventDate({ precision: dateDraftPrecision.value, date: dateDraftDay.value, endDate: dateDraftRangeEnd.value, year: dateDraftYear.value, month: dateDraftMonth.value, season: dateDraftSeason.value })
    if (dateDraftPrecision.value !== 'unknown' && next.datePrecision === 'unknown') {
      dateSaveError.value = 'Enter a valid date or year.'
      return
    }
  }
  await persistDateSelection(next)
}

async function persistDateSelection(next: { date: Date | null; endDate: Date | null; datePrecision: DatePrecision | null }) {
  if (isSavingDate.value) return
  dateSaveError.value = ''
  commitInlineHeaderEdits()
  isSavingDate.value = true
  try {
    if (await eventStore.saveEventDate(next)) closeDatePopover()
    else dateSaveError.value = 'Could not save the date. Please try again.'
  } finally {
    isSavingDate.value = false
  }
}

const peopleList = computed(() => eventStore.currentEvent?.people ?? [])
const visiblePeople = computed(() => peopleList.value.slice(0, 3))
const hiddenPeopleCount = computed(() => Math.max(peopleList.value.length - 3, 0))
const tagList = computed(() => eventStore.currentEvent?.tags ?? [])
const unmatchedPeopleList = computed(() => eventStore.currentEvent?.unmatchedPeople ?? [])
const unmatchedTagList = computed(() => eventStore.currentEvent?.unmatchedTags ?? [])
const hasUnmatchedSuggestions = computed(
  () => unmatchedPeopleList.value.length > 0 || unmatchedTagList.value.length > 0,
)
const primaryTagLabel = computed(() => {
  const tags = tagList.value
  if (!tags.length) return 'Add tags'
  if (tags.length === 1) return tags[0]
  return `${tags[0]} +${tags.length - 1}`
})
const headerLocation = computed(() => eventStore.currentEvent?.locations?.[0] || 'Add location')

function getAvatarClass(name: string, index: number) {
  const normalized = name.trim().toLowerCase().charCodeAt(0) || 0
  if (normalized % 3 === 0) return 'avatar-caitie'
  if (normalized % 3 === 1) return 'avatar-paxton'
  if (normalized % 3 === 2) return 'avatar-sydney'
  if (index % 2 === 0) return 'avatar-fallback-a'
  return 'avatar-fallback-b'
}

function getAvatarImage(name: string) {
  return peopleAvatarByName.value[name] ?? null
}

function openCollaborateDrawer() {
  collaborationPanelRef.value?.openModal()
}

function handleCollaborationSavedSelection() {
  activeDetailTab.value = 'collaboration'
}

function handleUnreadCollaborationCountUpdate(value: number) {
  unreadCollaborateCount.value = value
}

function handleSelectedCollaborationCountUpdate(value: number) {
  selectedCollaborateItemsCount.value = value
}

function toggleDatePopover() {
  if (isDatePopoverOpen.value) return closeDatePopover()
  const event = eventStore.currentEvent
  if (!event) return
  dateDraftPrecision.value = event.datePrecision ?? (event.date ? 'legacy' : 'unknown')
  dateDraftDay.value = calendarDate(event.date, !event.datePrecision)
  dateDraftRangeEnd.value = calendarDate(event.endDate)
  const parts = dateDraftDay.value.split('-').map(Number)
  dateDraftYear.value = parts[0] || undefined
  dateDraftMonth.value = parts[1] || 1
  dateDraftSeason.value = ({ 3: 'spring', 6: 'summer', 9: 'fall', 12: 'winter' } as Record<number, Season>)[parts[1]] || 'summer'
  dateDraftStart.value = event.date ? dayjs(event.date).format('YYYY-MM-DDTHH:mm') : ''
  dateDraftEnd.value = event.endDate ? dayjs(event.endDate).format('YYYY-MM-DDTHH:mm') : ''
  dateSaveError.value = ''
  chooseDateDetail(dateDraftPrecision.value)
  isDatePopoverOpen.value = true
}

function closeDatePopover() {
  isDatePopoverOpen.value = false
}

function openLocationPicker() {
  isLocationPickerOpen.value = true
}

function handleLocationSelected(location: string) {
  eventStore.updateEventInMemory('locations', [location])
}

function handleClearLocation() {
  eventStore.updateEventInMemory('locations', [])
}

function handleContentChange(newContent: string) {
  // Real-time update without creating undo points
  eventStore.updateTextOnly(newContent)
}

function handleTextFocused() {
  isTextEditorFocused.value = true
  textEditingStartContent.value = eventStore.currentEvent?.text || ''
}

function handleTextBlurred(content: string) {
  isTextEditorFocused.value = false

  // Only create undo point if content actually changed
  if (content !== textEditingStartContent.value) {
    eventStore.updateTextWithUndo(content, textEditingStartContent.value)
  }
}

// Unified undo/redo handlers
function handleUndo() {
  const result = eventStore.undo()

  if (result.type === 'text') {
    // Restore text in the editor
    textEditor.value?.restoreSnapshot(result.data)
  } else if (result.type === 'event') {
    // Force reactive updates for all form fields
    if (eventTitleEl.value && eventStore.currentEvent) {
      eventTitleEl.value.innerText = eventStore.currentEvent.title || ''
      eventTitleText.value = eventStore.currentEvent.title || ''
    }
    if (eventDescriptionEl.value && eventStore.currentEvent) {
      eventDescriptionEl.value.innerText = eventStore.currentEvent.description || ''
      eventDescriptionText.value = eventStore.currentEvent.description || ''
    }

    // TextEditor will update automatically through reactive prop
  }
}

function handleRedo() {
  const result = eventStore.redo()

  if (result.type === 'text') {
    // Restore text in the editor
    textEditor.value?.restoreSnapshot(result.data)
  } else if (result.type === 'event') {
    // Force reactive updates for all form fields
    if (eventTitleEl.value && eventStore.currentEvent) {
      eventTitleEl.value.innerText = eventStore.currentEvent.title || ''
      eventTitleText.value = eventStore.currentEvent.title || ''
    }
    if (eventDescriptionEl.value && eventStore.currentEvent) {
      eventDescriptionEl.value.innerText = eventStore.currentEvent.description || ''
      eventDescriptionText.value = eventStore.currentEvent.description || ''
    }

    // TextEditor will update automatically through reactive prop
  }
}

async function handleDone() {
  commitInlineHeaderEdits()

  // If text editor is focused and has changes, save them first
  if (isTextEditorFocused.value) {
    const currentContent = textEditor.value?.getCurrentContent() || ''
    if (currentContent !== textEditingStartContent.value) {
      eventStore.updateTextWithUndo(currentContent, textEditingStartContent.value)
    }
  }

  if (!eventStore.isDirty) {
    clearEventFiles()
    eventStore.showEvent = false
    return
  }

  const success = await eventStore.saveChanges()
  if (success) {
    clearEventFiles()
    eventStore.showEvent = false
  }
}

function handleBack() {
  commitInlineHeaderEdits()

  // If text editor is focused and has changes, save them first
  if (isTextEditorFocused.value) {
    const currentContent = textEditor.value?.getCurrentContent() || ''
    if (currentContent !== textEditingStartContent.value) {
      eventStore.updateTextWithUndo(currentContent, textEditingStartContent.value)
    }
  }

  if (eventStore.isDirty) {
    unsavedChangesModal.value?.showModal()
  } else {
    eventStore.discardChanges()
    clearEventFiles()
    eventStore.showEvent = false
  }
}

async function saveAndExit() {
  commitInlineHeaderEdits()

  const success = await eventStore.saveChanges()
  if (success) {
    clearEventFiles()
    eventStore.showEvent = false
  }
}

function discardAndExit() {
  eventStore.discardChanges()
  clearEventFiles()
  eventStore.showEvent = false
}

function handleDeleteEvent() {
  eventStore.deleteEvent(eventStore.currentEvent?.id ? eventStore.currentEvent.id : null)
  eventStore.showEvent = false
}

const isPeopleDropdownOpen = ref(false)
const isTagDropdownOpen = ref(false)
const newTagName = ref('')

const peopleButtonRef = ref<HTMLElement>()
const tagButtonRef = ref<HTMLElement>()
const dropdownPosition = ref({ top: 0, left: 0 })
const tagDropdownPosition = ref({ top: 0, left: 0 })

function getDropdownPosition(
  buttonRef: HTMLElement,
  dropdownWidth: number,
  dropdownHeight: number,
) {
  const rect = buttonRef.getBoundingClientRect()
  const padding = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let top = rect.bottom + padding
  let left = rect.right - dropdownWidth

  if (left < 0) {
    left = rect.left
  }

  if (left + dropdownWidth > viewportWidth) {
    left = viewportWidth - dropdownWidth - padding
  }

  if (top + dropdownHeight > viewportHeight) {
    top = rect.top - dropdownHeight - padding
  }

  if (top < 0) {
    top = padding
  }

  left = Math.max(padding, Math.min(left, viewportWidth - dropdownWidth - padding))
  top = Math.max(padding, Math.min(top, viewportHeight - dropdownHeight - padding))

  return { top, left }
}

function togglePeopleDropdown(event: Event) {
  event.preventDefault()
  event.stopPropagation()

  if (!isPeopleDropdownOpen.value && peopleButtonRef.value) {
    dropdownPosition.value = getDropdownPosition(peopleButtonRef.value, 224, 240)
  }

  if (!isPeopleDropdownOpen.value) {
    closeTagDropdown()
  }

  isPeopleDropdownOpen.value = !isPeopleDropdownOpen.value
}

function closePeopleDropdown() {
  isPeopleDropdownOpen.value = false
}

function isPersonSelected(personName: string) {
  return peopleList.value.some((person) => person.toLowerCase() === personName.toLowerCase())
}

function hasTagSelected(tagName: string) {
  return tagList.value.some((tag) => tag.toLowerCase() === tagName.toLowerCase())
}

function removeUnmatchedPerson(personName: string) {
  if (!eventStore.currentEvent) return
  const nextUnmatchedPeople = unmatchedPeopleList.value.filter(
    (person) => person.toLowerCase() !== personName.toLowerCase(),
  )
  eventStore.updateEventInMemory('unmatchedPeople', nextUnmatchedPeople)
}

function removeUnmatchedTag(tagName: string) {
  if (!eventStore.currentEvent) return
  const nextUnmatchedTags = unmatchedTagList.value.filter(
    (tag) => tag.toLowerCase() !== tagName.toLowerCase(),
  )
  eventStore.updateEventInMemory('unmatchedTags', nextUnmatchedTags)
}

function addUnmatchedPerson(personName: string) {
  const trimmedName = personName.trim()
  if (!trimmedName || isPersonSelected(trimmedName)) {
    removeUnmatchedPerson(personName)
    return
  }

  eventStore.updateEventInMemory('people', [...peopleList.value, trimmedName])
  removeUnmatchedPerson(trimmedName)
}

function addUnmatchedTag(tagName: string) {
  const trimmedTag = tagName.trim()
  if (!trimmedTag || hasTagSelected(trimmedTag)) {
    removeUnmatchedTag(tagName)
    return
  }

  eventStore.updateEventInMemory('tags', [...tagList.value, trimmedTag])
  removeUnmatchedTag(trimmedTag)
}

function togglePersonSelection(personName: string) {
  const nextPeople = [...peopleList.value]
  const personIndex = nextPeople.findIndex(
    (person) => person.toLowerCase() === personName.toLowerCase(),
  )

  if (personIndex >= 0) {
    nextPeople.splice(personIndex, 1)
  } else {
    nextPeople.push(personName)
  }

  eventStore.updateEventInMemory('people', nextPeople)
}

function addAllPeopleToEvent() {
  eventStore.updateEventInMemory('people', [...availablePeople.value])
  closePeopleDropdown()
}

function toggleTagDropdown(event: Event) {
  event.preventDefault()
  event.stopPropagation()

  if (!isTagDropdownOpen.value && tagButtonRef.value) {
    tagDropdownPosition.value = getDropdownPosition(tagButtonRef.value, 280, 220)
  }

  if (!isTagDropdownOpen.value) {
    closePeopleDropdown()
  }

  isTagDropdownOpen.value = !isTagDropdownOpen.value
}

function closeTagDropdown() {
  isTagDropdownOpen.value = false
  newTagName.value = ''
}

function addTag() {
  const trimmedTag = newTagName.value.trim()
  if (!trimmedTag) return

  const nextTags = [...tagList.value]
  const hasTag = nextTags.some((tag) => tag.toLowerCase() === trimmedTag.toLowerCase())
  if (!hasTag) {
    eventStore.updateEventInMemory('tags', [...nextTags, trimmedTag])
    if (!availableTags.value.some((tag) => tag.toLowerCase() === trimmedTag.toLowerCase())) {
      availableTags.value = [...availableTags.value, trimmedTag].sort((a, b) => a.localeCompare(b))
    }
  }
  newTagName.value = ''
}

function toggleTagSelection(tagName: string) {
  const nextTags = [...tagList.value]
  const tagIndex = nextTags.findIndex((tag) => tag.toLowerCase() === tagName.toLowerCase())

  if (tagIndex >= 0) {
    nextTags.splice(tagIndex, 1)
  } else {
    nextTags.push(tagName)
  }

  eventStore.updateEventInMemory('tags', nextTags)
}

function removeTag(tagToRemove: string) {
  const nextTags = tagList.value.filter((tag) => tag !== tagToRemove)
  eventStore.updateEventInMemory('tags', nextTags)
}

onMounted(() => {
  lightbox = new PhotoSwipeLightbox({
    pswpModule: () => import('photoswipe'),
  })

  lightbox.on('uiRegister', () => {
    lightbox?.pswp?.ui?.registerElement({
      name: 'delete-button',
      order: 8,
      isButton: true,
      tagName: 'button',
      className: 'pswp__button pswp__button--delete',
      ariaLabel: 'Delete image',
      html: '',
      onInit: (el) => {
        render(
          h(IconTrash, {
            size: 18,
            stroke: '2.2',
          }),
          el,
        )

        Object.assign(el.style, {
          color: '#fff',
          opacity: '1',
          width: '36px',
          height: '36px',
          borderRadius: '9999px',
          background: 'transparent',
          border: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '10px',
          boxShadow: 'none',
        })
      },
      onClick: () => {
        openDeleteImageConfirm(selectedImageIndex.value)
      },
    })
  })

  lightbox.on('change', () => {
    if (!lightbox?.pswp) {
      return
    }

    selectedImageIndex.value = lightbox.pswp.currIndex
    void ensureImageLoadedForLightbox(selectedImageIndex.value)
    void preloadAdjacentImages(selectedImageIndex.value)
  })

  lightbox.init()
})

onBeforeUnmount(() => {
  lightbox?.destroy()
  lightbox = null
  dimensionCache.clear()
  photoSwipeItems.value = []
})
</script>

<template>
  <div class="h-full flex flex-col">
    <div class="flex items-center sticky top-0 bg-base-100 pl-1 pr-2 pt-4">
      <button @click="handleBack" class="text-primary flex items-center">
        <span class="mr-2">
          <IconChevronLeft />
        </span>
        Back
      </button>
      <div class="grow flex justify-end items-center gap-2">
        <button
          v-show="eventStore.isDirty"
          @click="handleUndo"
          :disabled="!eventStore.canUndo"
          class="text-primary flex items-center disabled:text-gray-400"
        >
          <IconArrowBackUp />
        </button>
        <button
          v-show="eventStore.isDirty"
          @click="handleRedo"
          :disabled="!eventStore.canRedo"
          class="text-primary flex items-center disabled:text-gray-400"
        >
          <IconArrowForwardUp />
        </button>
        <button
          v-show="eventStore.isDirty"
          @click="handleDone"
          :disabled="!eventStore.isDirty"
          class="flex items-center ml-2 mr-1 transition-colors"
          :class="eventStore.isDirty ? 'text-primary font-bold' : 'text-gray-400'"
        >
          Done
        </button>
        <div tabindex="0" role="button" class="btn btn-ghost btn-sm p-0 text-primary">
          <IconShare2 />
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm p-0 text-primary relative overflow-visible"
          @click="openCollaborateDrawer"
        >
          <IconMessageCircle />
          <span
            v-if="unreadCollaborateCount > 0"
            class="badge badge-primary badge-xs absolute -right-1 -top-1 min-w-4 px-1"
          >
            {{ unreadCollaborateCount }}
          </span>
        </button>
        <div class="dropdown dropdown-end">
          <div tabindex="0" role="button" class="btn btn-ghost btn-sm p-0 text-primary">
            <IconDotsCircleHorizontal />
          </div>
          <ul
            tabindex="0"
            class="menu dropdown-content bg-base-200 rounded-box w-56 p-2 shadow z-50"
          >
            <li>
              <a>
                <IconShare />
                Share
              </a>
            </li>
            <li>
              <a @click="deleteEventModal?.showModal()">
                <IconTrash />
                Delete
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <section class="p-2 pb-0 w-full">
      <div class="flex flex-col">
        <div
          ref="eventTitleEl"
          contentEditable="plaintext-only"
          translate="no"
          data-virtualkeyboard="true"
          @focus="onTitleFocus"
          @blur="onTitleBlur"
          class="text-2xl w-full focus:outline-none focus:ring-0 caret-brand-600"
          @keyup.enter="changeText()"
        >
          {{ eventStore.currentEvent?.title ? eventStore.currentEvent?.title : 'Untitled' }}
        </div>
        <div
          ref="eventDescriptionEl"
          contentEditable="plaintext-only"
          translate="no"
          data-virtualkeyboard="true"
          data-placeholder="Add description"
          @focus="onDescriptionFocus"
          @blur="onDescriptionBlur"
          class="editable-description text-sm w-full focus:outline-none focus:ring-0 caret-brand-600 text-base-content/70 mb-2"
        >
          {{ eventStore.currentEvent?.description || '' }}
        </div>
        <div
          v-if="(parentEventLabel && parentEventId) || childEventCount > 0"
          class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium tracking-wide text-base-content/60"
        >
          <router-link
            v-if="parentEventLabel && parentEventId"
            :to="`/event/${parentEventId}`"
            class="hover:text-base-content/80 hover:underline"
          >
            Part of {{ parentEventLabel }}
          </router-link>
          <div v-if="childEventCount > 0">
            {{ childEventCount }} {{ childEventCount === 1 ? 'related event' : 'related events' }}
          </div>
        </div>
        <div v-if="tripAnchorDestination || linkedTripTitle" class="mt-2 text-sm text-base-content/70">
          <div v-if="eventStore.currentEvent?.afterEventEndId && linkedTripTitle">
            After the end of
            <router-link :to="`/event/${eventStore.currentEvent.afterEventEndId}`" class="text-primary underline">{{ linkedTripTitle }}</router-link>
            <button type="button" class="btn btn-ghost btn-xs ml-2" @click="selectTripAnchor(null)">Remove link</button>
          </div>
          <template v-else-if="tripAnchorDestination">
            <p>“After our trip to {{ tripAnchorDestination }}” refers to an earlier event. Choose the trip if it is already on your timeline.</p>
            <div v-if="tripAnchorCandidates.length" class="flex flex-wrap gap-2 mt-2">
              <button v-for="candidate in tripAnchorCandidates" :key="candidate.id" type="button" class="btn btn-outline btn-xs" @click="selectTripAnchor(candidate.id)">
                {{ candidate.title }}{{ candidate.endDate || candidate.date ? ` (${dayjs(candidate.endDate || candidate.date).format('MMM D, YYYY')})` : '' }}
              </button>
            </div>
            <p v-else class="text-xs mt-1">No matching trip found. You can leave the time reference unresolved.</p>
          </template>
        </div>
        <div class="flex flex-col gap-2 py-1">
          <div class="flex items-center justify-between gap-3 text-primary">
            <button
              type="button"
              class="btn btn-ghost btn-sm justify-start min-h-0 h-auto px-0 normal-case font-semibold text-left text-primary"
              @click="toggleDatePopover"
            >
              <span class="flex items-center gap-2">
                <IconCalendarEvent />
                <span class="text-sm">{{ headerDateLabel }}</span>
              </span>
            </button>
            <button
              type="button"
              class="btn btn-ghost btn-sm justify-start min-h-0 h-auto px-0 normal-case font-semibold text-left text-primary"
              @click="openLocationPicker"
            >
              <span class="flex items-center gap-1 text-sm font-semibold text-primary">
                <IconMapPinFilled />
                <span>{{ headerLocation }}</span>
              </span>
            </button>
          </div>
          <div class="h-1 w-full rounded-full bg-primary/65"></div>
        </div>
        <p v-if="eventStore.captureProcessingError" role="status" class="text-sm text-base-content/70 mt-3">{{ eventStore.captureProcessingError }}</p>
        <div v-if="pendingDateClarification" class="mt-3 flex items-start gap-3" role="group" aria-label="Question from Dot" data-testid="date-clarification">
          <div class="w-12 sm:w-16 shrink-0" data-testid="dot-avatar">
            <DotAvatar state="questioning" decorative />
          </div>
          <div class="dot-speech-bubble min-w-0 flex-1 space-y-2 rounded-2xl bg-base-200 p-3">
            <p class="text-sm">{{ dateClarificationQuestion(pendingDateClarification, eventStore.currentEvent) }}</p>
            <div class="flex flex-wrap gap-2">
              <template v-if="pendingDateClarification.kind === 'month_choices'">
                <button v-for="option in pendingDateClarification.monthOptions" :key="`${option.year}-${option.month}`" type="button" class="btn btn-primary btn-sm" :disabled="isSavingDate || isDismissingClarification" @click="chooseClarificationMonth(option.month, option.year)">{{ formatEventDate(resolveEventDate({ precision: 'month', ...option })) }}</button>
              </template>
              <template v-if="clarificationYearOptions(pendingDateClarification, eventStore.currentEvent).length > 0">
                <button v-for="year in clarificationYearOptions(pendingDateClarification, eventStore.currentEvent)" :key="year" type="button" class="btn btn-primary btn-sm" :disabled="isSavingDate || isDismissingClarification" @click="chooseClarificationYear(year)">{{ year }}</button>
              </template>
              <button v-if="pendingDateClarification.kind === 'month_day' && clarificationYearOptions(pendingDateClarification, eventStore.currentEvent).length > 0" type="button" class="btn btn-ghost btn-sm" :disabled="isSavingDate || isDismissingClarification" @click="answerDateClarification">Another year</button>
              <button v-if="pendingDateClarification.kind !== 'month_choices' && clarificationYearOptions(pendingDateClarification, eventStore.currentEvent).length === 0" type="button" class="btn btn-primary btn-sm" @click="answerDateClarification">{{ pendingDateClarification.kind === 'month_day' ? 'Add year' : 'Add date' }}</button>
              <button type="button" class="btn btn-ghost btn-sm" :disabled="isDismissingClarification || isSavingDate" @click="maybeLater">{{ ['month_day', 'month_choices'].includes(pendingDateClarification.kind) || clarificationYearOptions(pendingDateClarification, eventStore.currentEvent).length > 0 ? 'Not sure' : 'Maybe later' }}</button>
            </div>
            <p v-if="clarificationError" role="alert" class="text-error text-sm">{{ clarificationError }}</p>
          </div>
        </div>
      </div>
      <Teleport to="body">
        <div
          class="modal modal-bottom sm:modal-middle"
          :class="{ 'modal-open': isDatePopoverOpen }"
          role="dialog"
          aria-label="Edit date"
          :aria-modal="isDatePopoverOpen ? 'true' : undefined"
          :aria-hidden="!isDatePopoverOpen"
          :inert="!isDatePopoverOpen"
        >
          <div class="modal-box">
            <h3 class="font-semibold mb-3">Edit date</h3>
            <p v-if="pendingDateClarification" class="text-sm mb-3">{{ dateClarificationQuestion(pendingDateClarification, eventStore.currentEvent) }}</p>
            <div v-if="dateShortcuts.length && ['choose', 'advanced'].includes(dateEditorStep)" class="mb-4 space-y-2">
              <p class="text-xs font-medium text-base-content/70">Possible dates · tap to use</p>
              <div class="flex flex-wrap gap-2" data-testid="date-shortcuts">
                <button v-for="date in dateShortcuts" :key="formatEventDate(date)" type="button" class="btn btn-primary btn-sm" :disabled="isSavingDate" @click="persistDateSelection(date)">{{ formatEventDate(date) }}</button>
              </div>
            </div>
            <div v-if="dateEditorStep === 'choose'" class="space-y-2 mb-4" role="group" aria-label="Date detail">
              <p class="text-sm mb-3">How well do you remember?</p>
              <button type="button" class="btn btn-outline h-14 w-full justify-start rounded-xl" @click="chooseDateDetail('year')"><IconCalendar :size="22" aria-hidden="true" /><span>Just year</span><IconChevronRight class="ml-auto opacity-50" :size="18" aria-hidden="true" /></button>
              <button type="button" class="btn btn-outline h-14 w-full justify-start rounded-xl" @click="chooseDateDetail('season')"><IconSun :size="22" aria-hidden="true" /><span>Season &amp; year</span><IconChevronRight class="ml-auto opacity-50" :size="18" aria-hidden="true" /></button>
              <button type="button" class="btn btn-outline h-14 w-full justify-start rounded-xl" @click="chooseDateDetail('day')"><IconCalendarEvent :size="22" aria-hidden="true" /><span>Exact date</span><IconChevronRight class="ml-auto opacity-50" :size="18" aria-hidden="true" /></button>
            </div>
            <div v-else class="flex items-center justify-between gap-3 rounded-xl bg-base-200 px-3 py-2 mb-4">
              <span class="flex min-w-0 items-center gap-2 text-sm font-medium"><component :is="dateDetailIcon" :size="22" class="shrink-0" aria-hidden="true" />{{ dateDetailLabel }}</span>
              <button type="button" class="btn btn-ghost btn-sm" aria-label="Change date detail" @click="dateEditorStep = 'choose'">Change</button>
            </div>
            <div v-if="dateDraftPrecision === 'season' && ['year', 'review'].includes(dateEditorStep)" class="flex items-center justify-between rounded-xl bg-base-200 px-3 py-2 mb-3">
              <span class="flex items-center gap-2 text-sm"><component :is="selectedSeasonIcon" :size="20" class="shrink-0" aria-hidden="true" />Season: {{ selectedSeasonLabel }}</span>
              <button type="button" class="btn btn-ghost btn-sm" @click="dateEditorStep = 'season'">Change season</button>
            </div>
            <div v-if="dateDraftPrecision === 'month' && ['year', 'review'].includes(dateEditorStep)" class="flex items-center justify-between rounded-xl bg-base-200 px-3 py-2 mb-3">
              <span class="flex items-center gap-2 text-sm"><IconCalendarMonth :size="20" class="shrink-0" aria-hidden="true" />Month: {{ new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, dateDraftMonth - 1, 1))) }}</span>
              <button type="button" class="btn btn-ghost btn-sm" @click="dateEditorStep = 'month'">Change month</button>
            </div>
            <details class="mb-3" :open="dateMoreOptionsOpen" @toggle="dateMoreOptionsOpen = ($event.target as HTMLDetailsElement).open">
              <summary class="cursor-pointer text-sm text-base-content/70 py-1">More date options</summary>
              <label class="block mb-3">
                <span class="block text-sm mb-1">Date precision</span>
                <select v-model="dateDraftPrecision" class="select w-full" aria-label="Date precision" @change="chooseDateDetail(dateDraftPrecision)">
                  <option v-if="pendingDateClarification?.kind === 'month_day'" value="month_day">Add year to {{ pendingDateClarification.sourceText }}</option>
                  <option v-if="hasLegacyDates" value="legacy">Existing date / duration</option>
                  <option value="range">Approximate date range</option>
                  <option value="day">Exact date</option>
                  <option value="month">Month and year</option>
                  <option value="season">Season and year</option>
                  <option value="year">Year only</option>
                  <option value="unknown">Not sure yet</option>
                </select>
              </label>
            </details>
            <label v-if="dateEditorStep === 'date'" class="block">
              <span class="block text-sm mb-1">Date</span>
              <input type="date" class="input w-full" v-model="dateDraftDay" aria-label="Exact date" @change="dateDraftDay && (dateEditorStep = 'review')" />
            </label>
            <template v-if="dateDraftPrecision === 'range' && dateEditorStep === 'advanced'">
              <label class="block mb-2">Earliest possible date<input type="date" class="input w-full" v-model="dateDraftDay" aria-label="Earliest possible date" /></label>
              <label class="block">Latest possible date<input type="date" class="input w-full" v-model="dateDraftRangeEnd" aria-label="Latest possible date" /></label>
            </template>
            <template v-if="dateDraftPrecision === 'legacy' && dateEditorStep === 'advanced'">
              <label class="block mb-2">Start<input type="datetime-local" class="input w-full" v-model="dateDraftStart" /></label>
              <label class="block">End<input type="datetime-local" class="input w-full" v-model="dateDraftEnd" /></label>
              <button type="button" class="btn btn-ghost btn-sm" @click="dateDraftEnd = ''">Clear end</button>
            </template>
            <div v-if="dateEditorStep === 'season'" class="grid grid-cols-2 gap-3 mb-4" role="group" aria-label="Season">
              <button v-for="season in seasonChoices" :key="season.value" type="button" class="btn btn-outline h-20 flex-col gap-2 rounded-xl" :class="dateDraftSeason === season.value ? 'btn-primary' : ''" :aria-pressed="dateDraftSeason === season.value" @click="dateDraftSeason = season.value; dateEditorStep = 'year'"><component :is="season.icon" :size="20" aria-hidden="true" />{{ season.label }}</button>
            </div>
            <div v-if="dateEditorStep === 'month'" class="grid grid-cols-3 gap-2 mb-4" role="group" aria-label="Month">
              <button v-for="month in monthChoices" :key="month.value" type="button" class="btn h-12 px-1" :class="dateDraftMonth === month.value ? 'btn-primary' : 'btn-outline'" :aria-pressed="dateDraftMonth === month.value" @click="dateDraftMonth = month.value; dateEditorStep = 'year'">{{ month.label }}</button>
            </div>
            <DateYearPicker v-if="dateEditorStep === 'year'" v-model="dateDraftYear" :key="eventStore.currentEvent?.id" @select="dateEditorStep = 'review'" />
            <div v-if="datePreview?.date && ['review', 'year'].includes(dateEditorStep)" class="rounded-xl bg-base-200 p-4 mt-4 space-y-2" data-testid="date-preview">
              <p class="text-xs text-base-content/70">Save this date</p>
              <p class="font-semibold">{{ formatEventDate(datePreview) }}</p>
              <button v-if="dateEditorStep === 'review'" type="button" class="btn btn-ghost btn-sm px-0" @click="dateEditorStep = dateDraftPrecision === 'day' ? 'date' : 'year'">{{ dateDraftPrecision === 'day' ? 'Change exact date' : 'Change year' }}</button>
            </div>
            <div class="flex flex-wrap gap-2 mt-3">
              <button v-if="eventStore.currentEvent?.datePrecision === 'range'" type="button" class="btn btn-ghost btn-sm" :disabled="isSavingDate" @click="persistDateSelection(resolveEventDate({ precision: 'range', date: calendarDate(eventStore.currentEvent.date), endDate: calendarDate(eventStore.currentEvent.endDate) }))">Leave it approximate</button>
              <button type="button" class="btn btn-ghost btn-sm" :disabled="isSavingDate" @click="persistDateSelection(resolveEventDate({ precision: 'unknown' }))">Not sure yet</button>
            </div>
            <p v-if="dateDraftPrecision === 'season' && dateDraftSeason === 'winter'" class="text-xs mt-2 opacity-70">Winter starts in December of this year and ends in February of the next.</p>
            <p v-if="hasLegacyDates && dateDraftPrecision !== 'legacy'" class="text-xs mt-2 opacity-70">Saving replaces the existing start and end dates.</p>
            <p v-if="dateSaveError" role="alert" class="text-error mt-2">{{ dateSaveError }}</p>
            <div class="modal-action mt-4">
              <button type="button" class="btn btn-sm" :disabled="isSavingDate" @click="closeDatePopover">Cancel</button>
              <button v-if="dateEditorStep === 'advanced' || (['year', 'review'].includes(dateEditorStep) && datePreview?.date)" type="button" class="btn btn-sm btn-primary" :disabled="isSavingDate" @click="saveDateDraft">{{ isSavingDate ? 'Saving…' : 'Save date' }}</button>
            </div>
          </div>
          <form method="dialog" class="modal-backdrop" @submit.prevent="closeDatePopover">
            <button @click="closeDatePopover">close</button>
          </form>
        </div>
      </Teleport>
      <EventLocationPicker
        v-model="isLocationPickerOpen"
        :current-location="eventStore.currentEvent?.locations?.[0] || ''"
        @select-location="handleLocationSelected"
        @clear-location="handleClearLocation"
      />
      <div class="flex items-center justify-between gap-3 w-full min-h-12 py-2">
        <div class="flex-shrink-0">
          <div
            ref="peopleButtonRef"
            class="flex items-center gap-3 rounded-full w-fit cursor-pointer transition-opacity hover:opacity-85"
            @click.stop="togglePeopleDropdown"
          >
            <div class="flex -space-x-2">
              <div
                v-for="(person, index) in visiblePeople"
                :key="person + index"
                class="people-avatar w-8 h-8 rounded-full border-2 border-base-100 flex items-center justify-center text-xs font-semibold"
                :class="getAvatarClass(person, index)"
              >
                <img
                  v-if="getAvatarImage(person)"
                  :src="getAvatarImage(person) || ''"
                  :alt="`${person} avatar`"
                  class="avatar-photo w-full h-full rounded-full object-cover"
                />
                <span v-else>{{ getInitials(person) }}</span>
              </div>
              <div
                v-if="hiddenPeopleCount > 0"
                class="people-avatar avatar-overflow w-8 h-8 rounded-full border-2 border-base-100 flex items-center justify-center text-xs font-semibold"
              >
                +{{ hiddenPeopleCount }}
              </div>
            </div>
          </div>
        </div>
        <button
          type="button"
          ref="tagButtonRef"
          class="flex-shrink-0 flex items-center gap-2 bg-base-300 rounded-full w-fit px-3 py-1 text-sm"
          @click.stop="toggleTagDropdown"
        >
          <IconTag />
          {{ primaryTagLabel }}
        </button>
      </div>
      <div
        v-if="hasUnmatchedSuggestions"
        class="w-full rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3 mb-2"
      >
        <div class="text-xs font-semibold uppercase tracking-wide text-primary/80 mb-2">
          Assistant suggestions to add
        </div>
        <div v-if="unmatchedPeopleList.length > 0" class="mb-2">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-base-content/70 mb-1">
            People
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(personName, index) in unmatchedPeopleList"
              :key="`unmatched-person-${personName}`"
              class="flex items-center gap-2 rounded-full border border-base-300 bg-base-100 px-1 py-1"
            >
              <span class="flex items-center gap-2 pl-1 pr-1 text-sm">
                <span
                  class="people-avatar w-7 h-7 rounded-full border border-base-100 flex items-center justify-center text-[11px] font-semibold"
                  :class="getAvatarClass(personName, index)"
                >
                  {{ getInitials(personName) }}
                </span>
                <span>{{ personName }}</span>
              </span>
              <button
                type="button"
                class="btn btn-xs btn-primary"
                @click="addUnmatchedPerson(personName)"
              >
                Add
              </button>
              <button
                type="button"
                class="btn btn-xs btn-ghost"
                @click="removeUnmatchedPerson(personName)"
              >
                <IconX :size="14" />
              </button>
            </div>
          </div>
        </div>
        <div v-if="unmatchedTagList.length > 0">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-base-content/70 mb-1">
            Tags
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="tagName in unmatchedTagList"
              :key="`unmatched-tag-${tagName}`"
              class="flex items-center gap-2"
            >
              <span
                class="badge badge-outline rounded-full py-4 bg-secondary text-secondary-content"
              >
                #{{ tagName }}
              </span>
              <button
                type="button"
                class="btn btn-xs btn-primary"
                @click="addUnmatchedTag(tagName)"
              >
                Add
              </button>
              <button
                type="button"
                class="btn btn-xs btn-ghost"
                @click="removeUnmatchedTag(tagName)"
              >
                <IconX :size="14" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Teleport to="body">
        <ul
          v-show="isPeopleDropdownOpen"
          class="menu bg-base-100 rounded-box w-56 p-2 shadow-xl border border-base-300 fixed z-50"
          :style="{
            top: dropdownPosition.top + 'px',
            left: dropdownPosition.left + 'px',
          }"
        >
          <li v-for="personName in availablePeople" :key="personName">
            <label class="justify-between p-2 rounded-full cursor-pointer">
              <div
                class="w-8 h-8 rounded-full flex justify-center items-center bg-base-300 text-base-content font-semibold text-md"
              >
                {{ getInitials(personName) }}
              </div>
              <span class="grow ml-2">{{ personName }}</span>
              <input
                type="checkbox"
                class="checkbox"
                :checked="isPersonSelected(personName)"
                @change="togglePersonSelection(personName)"
              />
            </label>
          </li>
          <li v-if="availablePeople.length === 0" class="px-2 py-1 text-sm text-base-content/70">
            Add people from the People page first.
          </li>
          <li class="flext items-center justify-center">
            <router-link
              to="/people"
              @click="closePeopleDropdown"
              class="btn btn-circle flex items-center gap-2"
            >
              <IconUserPlus />
            </router-link>
          </li>
          <li class="py-2">
            <button
              class="btn btn-accent"
              :disabled="availablePeople.length === 0"
              @click="addAllPeopleToEvent"
            >
              <IconListCheck />
              Add All
            </button>
          </li>
        </ul>

        <!-- Click outside to close dropdown -->
        <div
          v-if="isPeopleDropdownOpen"
          class="fixed inset-0 z-40"
          @click="closePeopleDropdown"
        ></div>
      </Teleport>
      <Teleport to="body">
        <div
          v-show="isTagDropdownOpen"
          class="bg-base-100 rounded-box w-72 p-3 shadow-xl border border-base-300 fixed z-50"
          :style="{
            top: tagDropdownPosition.top + 'px',
            left: tagDropdownPosition.left + 'px',
          }"
        >
          <h3 class="text-sm font-semibold mb-2">Tags</h3>
          <div v-if="availableTags.length > 0" class="flex flex-wrap gap-2 mb-3">
            <button
              v-for="tagName in availableTags"
              :key="tagName"
              type="button"
              class="badge gap-1"
              :class="hasTagSelected(tagName) ? 'badge-primary' : 'badge-outline'"
              @click.stop="toggleTagSelection(tagName)"
            >
              {{ tagName }}
            </button>
          </div>
          <div class="flex flex-wrap gap-2 mb-3">
            <span v-for="tag in tagList" :key="tag" class="badge badge-outline badge-primary gap-1">
              {{ tag }}
              <button
                v-if="tagList.length > 0"
                type="button"
                class="font-bold leading-none"
                @click.stop="removeTag(tag)"
              >
                x
              </button>
            </span>
          </div>
          <form class="flex items-center gap-2" @submit.prevent="addTag">
            <input
              v-model="newTagName"
              type="text"
              class="input input-sm input-bordered flex-1"
              placeholder="Add a tag"
            />
            <button type="submit" class="btn btn-sm btn-primary">Add</button>
          </form>
        </div>

        <div v-if="isTagDropdownOpen" class="fixed inset-0 z-40" @click="closeTagDropdown"></div>
      </Teleport>

      <div class="flex items-center gap-2 w-full overflow-x-auto no-scrollbar pb-2">
        <!-- Display existing images -->
        <div
          v-for="(image, index) in eventImages"
          :key="index"
          class="relative flex-shrink-0"
          style="width: 100px; height: 100px"
        >
          <img
            :src="image"
            alt="Event image"
            class="rounded-sm object-cover cursor-pointer hover:opacity-80 transition-opacity w-full h-full"
            @click="handleOpenImageModal(index)"
          />
        </div>

        <div
          v-for="placeholderIndex in pendingImageUploads"
          :key="`uploading-${placeholderIndex}`"
          class="relative flex-shrink-0 bg-base-200 border border-base-300 rounded-sm overflow-hidden"
          style="width: 100px; height: 100px"
        >
          <div class="absolute inset-0 animate-pulse bg-base-300/70"></div>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="loading loading-spinner loading-sm text-base-content/70"></span>
          </div>
        </div>

        <!-- Add image button -->
        <button
          @click="triggerImageUpload"
          class="flex-shrink-0 bg-base-200 hover:bg-base-100 border flex items-center justify-center rounded-sm transition-colors"
          style="width: 100px; height: 100px"
        >
          <div class="text-center">
            <IconPhotoPlus class="mx-auto" stroke="1" />
            <div class="text-xs mt-1">
              {{ isUploadingImages ? 'Uploading...' : 'Add Photos' }}
            </div>
          </div>
        </button>

        <!-- Hidden file input with multiple attribute -->
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          multiple
          @change="handleImageUpload"
          class="hidden"
        />
      </div>
    </section>

    <section class="flex-1 min-h-0 flex flex-col">
      <div class="px-2 py-2">
        <div class="join w-full">
          <button
            type="button"
            class="btn join-item flex-1 btn-sm"
            :class="activeDetailTab === 'event' ? 'btn-primary' : 'btn-outline'"
            @click="activeDetailTab = 'event'"
          >
            Event Details
          </button>
          <button
            type="button"
            class="btn join-item flex-1 btn-sm"
            :class="activeDetailTab === 'collaboration' ? 'btn-primary' : 'btn-outline'"
            @click="activeDetailTab = 'collaboration'"
          >
            Saved Collaboration
            <span
              v-if="selectedCollaborateItemsCount > 0"
              class="badge badge-neutral badge-xs ml-2"
            >
              {{ selectedCollaborateItemsCount }}
            </span>
          </button>
        </div>
      </div>

      <div v-show="activeDetailTab === 'event'" class="flex-1 min-h-0">
        <TextEditor
          ref="textEditor"
          v-if="eventStore.currentEvent"
          :key="eventStore.currentEvent.id"
          :inputText="eventStore.currentEvent?.text || ''"
          @content-change="handleContentChange"
          @text-focused="handleTextFocused"
          @text-blurred="handleTextBlurred"
        />
      </div>

      <EventCollaborationPanel
        ref="collaborationPanelRef"
        :show-saved="activeDetailTab === 'collaboration'"
        @update-unread-count="handleUnreadCollaborationCountUpdate"
        @update-selected-count="handleSelectedCollaborationCountUpdate"
        @saved-selection="handleCollaborationSavedSelection"
      />
    </section>

    <!-- Unsaved Changes Modal -->
    <dialog ref="unsavedChangesModal" class="modal">
      <div class="modal-box">
        <h3 class="text-lg font-bold">Unsaved Changes</h3>
        <p class="py-4">You have unsaved changes. What would you like to do?</p>
        <div class="modal-action">
          <form method="dialog">
            <div class="flex gap-2">
              <button class="btn btn-primary" @click="saveAndExit">Save & Exit</button>
              <button class="btn btn-error" @click="discardAndExit">Discard</button>
              <button class="btn">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </dialog>

    <!-- Delete Image Modal -->
    <dialog ref="deleteImageConfirmModal" class="modal">
      <div class="modal-box">
        <h3 class="text-lg font-bold">Delete this image?</h3>
        <p class="py-4">This image will be removed from the event and cannot be undone.</p>
        <div class="modal-action">
          <form method="dialog">
            <button class="btn">Cancel</button>
          </form>
          <button class="btn btn-error" @click="confirmDeleteImage">Delete</button>
        </div>
      </div>
    </dialog>

    <!-- Delete Event Modal -->
    <dialog ref="deleteEventModal" class="modal">
      <div class="modal-box">
        <h3 class="text-lg font-bold">Delete This Event?</h3>
        <p class="py-4">Are you sure you want to delete this event? It cannot be undone.</p>
        <div class="modal-action">
          <form method="dialog">
            <div class="flex gap-2">
              <button class="btn btn-error" @click="handleDeleteEvent">Delete</button>
              <button class="btn">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </dialog>
  </div>
</template>

<style scoped>
.dot-speech-bubble {
  position: relative;
}

.dot-speech-bubble::before {
  content: '';
  position: absolute;
  top: 1.125rem;
  left: -0.375rem;
  width: 0.75rem;
  height: 0.75rem;
  background: inherit;
  border-radius: 0 0 0 2px;
  transform: rotate(45deg);
  pointer-events: none;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.people-avatar {
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.15);
  overflow: hidden;
}

.avatar-photo {
  display: block;
}

.avatar-overflow {
  background: #d7dfd1;
  color: #3f4a3d;
}

.avatar-caitie {
  background: linear-gradient(145deg, #ffe7d7 0%, #f8c8a9 100%);
  color: #5b3b2a;
}

.avatar-paxton {
  background: linear-gradient(145deg, #d8e6ff 0%, #a9c7f8 100%);
  color: #1f3558;
}

.avatar-sydney {
  background: linear-gradient(145deg, #ffd9eb 0%, #f2b8d4 100%);
  color: #6e2b4a;
}

.avatar-fallback-a {
  background: linear-gradient(145deg, #f3d5ff 0%, #e0b0f4 100%);
  color: #6a2963;
}

.avatar-fallback-b {
  background: linear-gradient(145deg, #d8f4ee 0%, #a9e2d3 100%);
  color: #1f5f52;
}

.editable-description:empty::before {
  content: attr(data-placeholder);
  color: rgb(100 116 139 / 0.7);
}
</style>

<style>
.pswp__button--delete {
  color: #fff !important;
  opacity: 1 !important;
  width: 36px !important;
  height: 36px !important;
  border-radius: 9999px !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.pswp__button--delete:hover {
  opacity: 0.85 !important;
}

.pswp__button--delete:focus-visible {
  outline: 2px solid #fff !important;
  outline-offset: 2px !important;
}

.pswp__button--delete svg {
  width: 18px !important;
  height: 18px !important;
  margin-top: 3px !important;
}
</style>
